import { CheckOutlined, CloseOutlined, StopOutlined, SwapOutlined } from '@ant-design/icons';
import { Card, Col, Space, Spin, Timeline } from 'antd';
import { Helmet } from 'react-helmet-async';
import { Outlet } from 'react-router-dom';

import useAPIQuery from '../../api/useAPIQuery';
import useInfiniteAPIQuery from '../../api/useInfiniteAPIQuery';
import ErrorResult from '../../components/ErrorResult';
import PageContent from '../../components/PageContent';
import PageTitle from '../../components/PageTitle';
import RequestJobPostingTabs from '../../components/RequestJobPostingTabs/RequestJobPostingTabs';
import usePrefetchPages from '../../hooks/usePrefetchPages';
import useTypedParams from '../../hooks/useTypedParams';
import { withRouteChange } from '../../services/APM';
import { RequestJobPostingId } from '../../types';
import GeneralRequestJobPostingTimeline, {
  GeneralRequestJobPostingTimelineProps,
} from './GeneralRequestJobPostingTimeline';
import RequestJobPostingEditTimeline from './RequestJobPostingEditTimeline';
import RequestJobPostingFinishedTimeline from './RequestJobPostingFinishedTimeline';
import styles from './RequestJobPostingHistory.module.css';

const RequestJobPostingHistory = () => {
  const { id } = useTypedParams({ id: RequestJobPostingId });

  const requestJobPosting = useAPIQuery(`/request-job-postings/${id}/`);

  const requestJobPostingHistoryPages = useInfiniteAPIQuery([
    '/request-job-posting-history/',
    {
      params: {
        request_job_posting: id,
      },
    },
  ]);

  const requestJobPostingHistory = usePrefetchPages(
    requestJobPostingHistoryPages,
    (data) => data.results
  );

  const timelineItemIconRelation = {
    REQUEST_CREATED: <CheckOutlined />,
    REQUEST_CANCELED: <StopOutlined className={styles.canceledColor} />,
    STAGE_APPROVED: <SwapOutlined className={styles.successColor} />,
    STAGE_REJECTED: <CloseOutlined className={styles.errorColor} />,
  };

  return (
    <ErrorResult error={requestJobPosting.error || requestJobPostingHistoryPages.error}>
      <Spin spinning={requestJobPosting.isLoading || requestJobPostingHistoryPages.isLoading}>
        <Helmet>
          <title>
            Histórico da requisição | {requestJobPosting.data?.name || '...'} | Recrutamento e
            Seleção
          </title>
        </Helmet>
        <PageContent>
          <Outlet />
          <Card>
            <Space direction="vertical" size="middle">
              <Col span={24}>
                <RequestJobPostingTabs />
              </Col>
              <PageTitle title="Histórico" />
              <Timeline>
                {requestJobPostingHistory.map((historyEntry, index) => (
                  <div key={index}>
                    {[
                      'REQUEST_CREATED',
                      'REQUEST_CANCELED',
                      'STAGE_APPROVED',
                      'STAGE_REJECTED',
                    ].includes(historyEntry.type) && (
                      <GeneralRequestJobPostingTimeline
                        action={
                          historyEntry.type as GeneralRequestJobPostingTimelineProps['action']
                        }
                        stage={historyEntry.stage}
                        userName={
                          historyEntry.created_by.is_staff
                            ? 'Sistema'
                            : historyEntry.created_by.full_name
                        }
                        commentary={historyEntry.commentary}
                        createdDate={historyEntry.created_at}
                        timelineIcon={
                          timelineItemIconRelation[
                            historyEntry.type as GeneralRequestJobPostingTimelineProps['action']
                          ]
                        }
                      />
                    )}
                    {historyEntry.type === 'REQUEST_EDITED' && (
                      <RequestJobPostingEditTimeline
                        userName={
                          historyEntry.created_by.is_staff
                            ? 'Sistema'
                            : historyEntry.created_by.full_name
                        }
                        changedFields={
                          historyEntry.changed_fields as Record<string, string | number>
                        }
                        createdDate={historyEntry.created_at}
                      />
                    )}
                    {historyEntry.type === 'REQUEST_FINISHED' && (
                      <RequestJobPostingFinishedTimeline createdDate={historyEntry.created_at} />
                    )}
                  </div>
                ))}
              </Timeline>
            </Space>
          </Card>
        </PageContent>
      </Spin>
    </ErrorResult>
  );
};

export default withRouteChange()(RequestJobPostingHistory);
